/*
リストの要素をドラッグ&ドロップで並べ替えるためのコンポーネント。
並べ替えたい要素は必ず
  position: absolute;
とし、このコンポーネントに渡す
  targetClass
と同じクラス名を付けること。
*/
import React, { useState } from 'react';
import Draggable, { DraggableEvent, DraggableData, DraggableProps } from 'react-draggable';

import { deepCopy } from '../general';

type SortValueType = { order: number; }

type DraggableSortProps = {
  draggableProps: {[P in keyof DraggableProps]?: DraggableProps[P]};
  targetClass: string;
  id: string;
  onDrag?: (ev: DraggableEvent) => void;
  ids: string[];
  values: SortValueType[];
  setValues: (values: any[]) => void;
  setDraggingId: (id?: string) => void;
  inactive?: boolean;
}
const DraggableSort: React.FC<DraggableSortProps> = ({ children, draggableProps, targetClass, id, onDrag, ids, values, setValues, setDraggingId, inactive }) => {
  const [movingId, setMovingId] = useState<string>();

  const getTargetId = (draggingId: string, element: HTMLElement): string | undefined => {
    const elementClass = element.className;
    if (!element || typeof elementClass !== 'string') return;
    if (elementClass.includes(targetClass)) {
      if (element.id === draggingId) return;
      setMovingId(element.id);
      element.ontransitionend = () => setMovingId(undefined);
      return element.id;
    }
    else if (element.parentElement) return getTargetId(draggingId, element.parentElement);
    else return;
  }
  const dragStart = (ev: DraggableEvent, data: DraggableData) => {
    if (draggableProps.onStart) draggableProps.onStart(ev, data);
    document.body.style.cursor = 'grabbing';
  }
  const changeDisplayOrder = (ev: DraggableEvent, id: string) => {
    setDraggingId(id);
    const target = ev.target as HTMLElement;
    
    if (onDrag) onDrag(ev);
    if (inactive) return;

    const targetId = getTargetId(id, target);
    if (!targetId || targetId === movingId) return;

    const ix = ids.indexOf(id);
    const targetIx = ids.indexOf(targetId);

    const dragOrder = values[ix].order;
    const targetOrder = values[targetIx].order;
    const newValues = deepCopy(values);

    if (dragOrder < targetOrder) {
      ids.forEach((templateId, templateIx) => {
        const value = values[templateIx];
        if (templateId === id) newValues[templateIx] = { ...value, order: targetOrder };
        else if (templateId === targetId) newValues[templateIx] = { ...value, order: value.order - 1 };
        else if (dragOrder < value.order && value.order < targetOrder) newValues[templateIx] = { ...value, order: value.order - 1 };
      })
    } else {
      ids.forEach((templateId, templateIx) => {
        const value = values[templateIx];
        if (templateId === id) newValues[templateIx] = { ...value, order: targetOrder };
        else if (templateId === targetId) newValues[templateIx] = { ...value, order: value.order + 1 };
        else if (targetOrder < value.order && value.order < dragOrder) newValues[templateIx] = { ...value, order: value.order + 1 };
      })
    }

    setValues(newValues);
  }
  const dragStop = (ev: DraggableEvent, data: DraggableData) => {
    setDraggingId(undefined);
    setMovingId(undefined);
    document.body.style.cursor = 'auto';
    if (draggableProps.onStop) draggableProps.onStop(ev, data);
  }

  return (
    <Draggable position={draggableProps.position} handle={draggableProps.handle}
      onStart={dragStart}
      onDrag={(ev: DraggableEvent) => changeDisplayOrder(ev, id)}
      onStop={(ev: DraggableEvent, data: DraggableData) => dragStop(ev, data)}
      axis={draggableProps.axis}
      bounds={draggableProps.bounds}
      disabled={draggableProps.disabled}
    >
      { children }
    </Draggable>
  );
}

export default DraggableSort;
